"use client"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { StatusBadge } from "@/components/ui/status-badge"
import type { SubmissionScoringStatus } from "@/lib/exam-status-badges"
import { submissionScoringStatusChip } from "@/lib/exam-status-badges"
import { STATUS_BADGE_TONE_CLASS } from "@/lib/list-status-badges"
import {
  submissionQuestionCompletion,
  type AssessmentGradingState,
  type GradingSubmission,
} from "@/lib/mock/exam-grading"
import { initialsFromDisplayName } from "@/lib/initials-from-name"
import { stockPortraitUrl } from "@/lib/stock-portrait"
import { cn } from "@/lib/utils"

export const SUBMISSION_SCORING_STATUS_TONE = {
  "Not started": "warning",
  Unscored: "warning",
  "Partially scored": "warning",
  Scored: "success",
  "Score Released": "info",
  Discarded: "danger",
} as const satisfies Record<
  SubmissionScoringStatus,
  keyof typeof STATUS_BADGE_TONE_CLASS
>

export function submissionCompactNumber(
  row: GradingSubmission,
  index: number,
): number {
  const fromLabel = row.maskLabel.match(/#(\d+)/)?.[1]
  if (fromLabel) return Number.parseInt(fromLabel, 10)
  return index + 1
}

export function hubSubmissionScoringStatus(
  row: GradingSubmission,
): SubmissionScoringStatus {
  if (row.thrownOut) return "Discarded"
  switch (row.scoreStatus) {
    case "Scores Released":
      return "Score Released"
    case "Graded":
      return "Scored"
    case "Partially Evaluated":
      return "Partially scored"
    default:
      return "Unscored"
  }
}

export function SubmissionRowAvatar({ row }: { row: GradingSubmission }) {
  const initials = initialsFromDisplayName(row.studentName)

  return (
    <Avatar size="sm" shape="circle" className="pointer-events-none size-6 shrink-0">
      <AvatarImage
        src={stockPortraitUrl(`grading-${row.studentId}`)}
        alt=""
        referrerPolicy="no-referrer"
      />
      <AvatarFallback className="text-2xs font-semibold">{initials}</AvatarFallback>
    </Avatar>
  )
}

export function SubmissionCountAvatar({
  number,
  status,
}: {
  number: number
  status: SubmissionScoringStatus
}) {
  const tone = SUBMISSION_SCORING_STATUS_TONE[status]

  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full border font-semibold tabular-nums",
        "text-xs leading-none",
        STATUS_BADGE_TONE_CLASS[tone],
      )}
      aria-hidden="true"
    >
      #{number}
    </span>
  )
}

/** Scored rows show a checkmark plus question completion (e.g. 8/8). */
export function submissionRosterStatusBadge(
  state: AssessmentGradingState,
  row: GradingSubmission,
) {
  const status = hubSubmissionScoringStatus(row)
  if (status === "Scored" || status === "Score Released") {
    const { scoredCount, totalCount } = submissionQuestionCompletion(state, row)
    return (
      <StatusBadge
        label={`${scoredCount}/${totalCount}`}
        tone={status === "Score Released" ? "info" : "success"}
        icon="fa-circle-check"
        size="sm"
      />
    )
  }
  return submissionScoringStatusChip(status, "sm")
}
